import { DateTime } from 'luxon';
import type { CollectionTicketClaimDTO } from '@/api/trafikportalen';
import { getPeriodStatus } from '@/helpers/periodStatus';

export interface PeriodRange {
  from: string;
  to: string;
}

const toInterval = (range: PeriodRange) => ({
  from: DateTime.fromISO(range.from),
  to: DateTime.fromISO(range.to),
});

export const periodsOverlap = (a: PeriodRange, b: PeriodRange): boolean => {
  const first = toInterval(a);
  const second = toInterval(b);
  if (
    !first.from.isValid ||
    !first.to.isValid ||
    !second.from.isValid ||
    !second.to.isValid
  ) {
    return false;
  }
  return first.from < second.to && second.from < first.to;
};

const isHistoricPeriod = (claim: {
  valid_from?: string;
  valid_to?: string;
}): boolean => getPeriodStatus(claim) === 'past';

const claimRange = (claim: CollectionTicketClaimDTO): PeriodRange | null =>
  claim.valid_from && claim.valid_to
    ? { from: claim.valid_from, to: claim.valid_to }
    : null;

/**
 * Current periods that overlap the given range. Historic periods are ignored.
 * Pass `excludeClaimId` when editing a period so it is not compared with itself.
 */
export const findOverlappingClaims = (
  range: PeriodRange,
  claims: CollectionTicketClaimDTO[] | undefined,
  excludeClaimId?: string,
): CollectionTicketClaimDTO[] =>
  (claims ?? []).filter((claim) => {
    if (claim.id === excludeClaimId || isHistoricPeriod(claim)) return false;
    const other = claimRange(claim);
    return other !== null && periodsOverlap(range, other);
  });

/** Ids of current periods that overlap at least one other current period. */
export const findOverlappingClaimIds = (
  claims: CollectionTicketClaimDTO[] | undefined,
): Set<string> => {
  const ids = new Set<string>();
  const list = (claims ?? []).filter((claim) => !isHistoricPeriod(claim));
  for (let i = 0; i < list.length; i++) {
    const a = claimRange(list[i]);
    if (!a) continue;
    for (let j = i + 1; j < list.length; j++) {
      const b = claimRange(list[j]);
      if (b && periodsOverlap(a, b)) {
        ids.add(list[i].id);
        ids.add(list[j].id);
      }
    }
  }
  return ids;
};
